import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as yup from 'yup';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { ChannelType } from 'helpers/enums';

interface IChannelContext {
    isLoading: boolean
    channel: Channel

    saveChannel: (channel: Channel) => void
}


export const channelSchema = yup.object({
    id: yup.string().default(''),
    channelCode: yup.string().required().default('').label('Code'),
    channelType: yup.string().required().default(ChannelType.Lengow).label('Channel type'),
    description: yup.string().default('').label('Description'),
    allCountries: yup.boolean().default(true).label('All countries'),
    countryCode: yup.string()
        .when('allCountries', {
            is: false,
            then: yup.string().default('').required().label('Country code'),
            otherwise: yup.string().default('').label('Country code')
        }).default(''),
    defaultCustomer: yup.string().default('').label('Default customer'),
    publicName: yup.string().default('').label('Public name'),
    sendFeesEmail: yup.boolean().default(false).label('Send fees email'),
    orderIdPrefix: yup.string().default('').label('order reference prefix'),
    erpVendorCode: yup.string().default('').required().label('Vendor code'),
    isDisabled: yup.boolean().default(false).label('Channel is disabled'),
    due: yup.object({
        dueOrderType: yup.string().default('').label('Giro'),
        causalCode: yup.string().default('').label('Causal code'),
        workOrderCode: yup.string().default('').label('Workorder Code'),
        customerAddressCode: yup.string().default('').label('Customer Address Code'),
        automaticOrderRelease: yup.boolean().default(false).label('Automatic order release')
    }),
    lengow: yup.object().when("channelType", {
        is: ChannelType.Lengow,
        then: yup.object({
            account: yup.string().required().default('').label('Lengow account Id'),
            marketplace: yup.string().required().default('').label('Lengow marketplace'),
            lengowChannelCode: yup.string().required().default('').label('Lengow channel code')
        }),
        otherwise: yup.object({
            account: yup.string().default('').label('Lengow account Id'),
            marketplace: yup.string().default('').label('Lengow marketplace'),
            lengowChannelCode: yup.string().default('').label('Lengow channel code')
        })
    })
});

type Channel = yup.InferType<typeof channelSchema>;

export let channelContext = React.createContext({} as IChannelContext);

let { Provider } = channelContext;

let ChannelProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    let { id } = useParams<{ id: string }>();
    let location = useLocation();
    let navigate = useNavigate();
    let { enqueueSnackbar } = useSnackbar();

    let isNew = location.pathname.includes("/addnew");

    let [isLoading, setLoading] = useState<boolean>(isNew === false);
    let [channel, setChannel] = useState<Channel>(channelSchema.getDefault());


    useEffect(() => {
        if (isNew === false)
            getChannel();

    }, [id, isNew])

    let getChannel = async () => {
        try {
            let res = await axios.get('/api/Channel/GetChannel', { params: { id } });

            const { item } = res.data;

            setChannel({
                ...item,
                erpVendorCode: item.erpVendorCode ?? channelSchema.getDefault().erpVendorCode
            });
        } catch (err) { }
        finally {
            setLoading(false);
        }
    }

    let saveChannel = async (value: Channel) => {
        try {
            await axios.post('/api/Channel/SaveChannel', value);
            enqueueSnackbar("Channel saved", { variant: "success" });
            navigate(`/admin/channel`);

        } catch (err) {
        }
    }

    return (
        <Provider
            value={{
                isLoading,
                channel,

                saveChannel
            }}
        >
            {children}
        </Provider>
    )
}

export default ChannelProvider;